import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap } from 'rxjs';

import { teamAll, teamNone, TeamsApiService } from '@ninety/mobile/platform/api';
import { SessionStorageService } from '@ninety/mobile/platform/services';

import { appActions } from '../../app/app.actions';
import { CurrentUserSelectors } from '../user-list/user-list-state.selectors';

import { TeamListStateActions } from './team-list-state.actions';
import { TeamSelectors } from './team-list-state.selectors';

@Injectable()
export class TeamListStateEffects {
  private readonly action$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly teamsApi = inject(TeamsApiService);

  initGlobalState_fetchTeams$ = createEffect(() =>
    this.action$.pipe(
      ofType(appActions.initGlobalState),
      map(() => TeamListStateActions.fetch())
    )
  );

  fetchTeamList$ = createEffect(() =>
    this.action$.pipe(
      ofType(TeamListStateActions.fetch),
      switchMap(() =>
        this.teamsApi.getTeamsLite().pipe(
          map(teams => TeamListStateActions.fetchSuccess({ teams })),
          catchError((error: unknown) => of(TeamListStateActions.fetchError({ error })))
        )
      )
    )
  );

  // Initial Team is unnecessary on mobile as we have a user-centric approach, not a team aproach.
  // Commenting this for now, but I believe it can be completely removed

  // setInitialTeam_trigger$ = createEffect(() =>
  //   this.action$.pipe(
  //     ofType(TeamListStateActions.fetchSuccess, UserListStateActions.setLoggedInUser),
  //     map(() => TeamListStateActions.setInitialTeam())
  //   )
  // );

  // setInitialTeam$ = createEffect(() =>
  //   this.action$.pipe(
  //     ofType(TeamListStateActions.setInitialTeam),
  //     concatLatestFrom(() => [
  //       this.store.select(CurrentUserSelectors.selectUser),
  //       this.store.select(TeamSelectors.selectAll),
  //     ]),
  //     map(([action, currentUser, teams]) => ({ teams, currentUser })),
  //     map(({ teams, currentUser }) => {
  //       if (!teams || !currentUser) return appActions.noop();

  //       // set default team as selected team when logging in, as long as the user still belongs to that team
  //       const sessionTeamId = this.sessionStorageService.get('lastAccessedTeamId');
  //       let initialTeamId: string | undefined = sessionTeamId ?? currentUser?.settings?.defaultTeamId;
  //       const userStillOnTeam = currentUser?.teams?.some((t: { teamId: string }) => t.teamId === initialTeamId);
  //       if (!userStillOnTeam && currentUser.teams && currentUser.teams.length > 0) {
  //         initialTeamId = currentUser?.teams[0].teamId;
  //       }

  //       const initialTeam = teams.find(team => team._id === initialTeamId);
  //       if (initialTeam) {
  //         this.sessionStorageService.set('lastAccessedTeamId', initialTeam._id);
  //       }
  //       return TeamListStateActions.getSelectedTeam({ id: initialTeamId });
  //     })
  //   )
  // );

  changeSelectedTeam$ = createEffect(() =>
    this.action$.pipe(
      ofType(TeamListStateActions.changeFilterBarTeam),
      concatLatestFrom(() => this.store.select(TeamSelectors.selectFilterBarTeamId)),
      filter(([{ id }, selectedTeamId]) => id !== selectedTeamId),
      map(([{ id }]) => TeamListStateActions.getSelectedTeam({ id }))
    )
  );

  getSelectedTeam$ = createEffect(() =>
    this.action$.pipe(
      ofType(TeamListStateActions.getSelectedTeam),
      switchMap(({ id }) => {
        if (!id) return of(appActions.noop());
        if (id === teamAll._id) return of(TeamListStateActions.getSelectedTeamSuccess({ team: teamAll }));
        if (id === teamNone._id) return of(TeamListStateActions.getSelectedTeamSuccess({ team: teamNone }));
        return this.teamsApi.getTeam(id).pipe(
          map(team => TeamListStateActions.getSelectedTeamSuccess({ team })),
          catchError((error: unknown) => of(TeamListStateActions.getSelectedTeamError({ error })))
        );
      })
    )
  );

  getSelectedTeamSuccess_UpdateLastTeam$ = createEffect(() =>
    this.action$.pipe(
      ofType(TeamListStateActions.getSelectedTeamSuccess),
      filter(({ team }) => ![teamAll._id, teamNone._id].includes(team._id as string)), // exclude all and none
      concatLatestFrom(() => [this.store.select(CurrentUserSelectors.selectUser)]),
      map(([{ team }, currentUser]) => {
        this.sessionStorageService.set('lastAccessedTeamId', team._id);
        // This may run before state service is initilized, if so just return
        if (!currentUser || !team) return appActions.noop();

        // ToDo: Convert to User Store when the store is ready
        if (currentUser.lastAccessedTeamId !== team._id) {
          // return UserActions.UpdateOne({ changes: { lastAccessedTeamId: team._id }});
        }
        return appActions.noop();
      })
    )
  );

  updateOne$ = createEffect(() =>
    this.action$.pipe(
      ofType(TeamListStateActions.updateOne),
      switchMap(({ id, update }) =>
        this.teamsApi.updateTeam(id, update).pipe(
          map(() => TeamListStateActions.updateOneSuccess({ id, update })),
          catchError((error: unknown) => of(TeamListStateActions.updateOneError({ error })))
        )
      )
    )
  );

  // Duplicate of above, but for props that are not on the TeamListModel.  Might be a better way to do this.
  updateTeam$ = createEffect(() =>
    this.action$.pipe(
      ofType(TeamListStateActions.updateTeam),
      switchMap(({ id, update }) =>
        this.teamsApi.updateTeam(id, update).pipe(
          map(() => TeamListStateActions.updateOneSuccess({ id, update })),
          catchError((error: unknown) => of(TeamListStateActions.updateOneError({ error })))
        )
      )
    )
  );
}
